/**
 * Deterministic Recovery Suggestion Engine (v1)
 *
 * Pure logic — no side effects, no API calls, no AI.
 * Computes "Move appointment → Dock X" suggestions for breached docks.
 *
 * Performance target: <5ms for ~200 appointments.
 */

import type { Appointment } from '../types/appointments';
import { SLA_WARN_MINUTES, SLA_CRITICAL_MINUTES } from './sla';
import { safeDateMs } from './cascadePrediction';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SeverityLevel = 'OVER_SLA' | 'CRITICAL';

export interface BreachedDock {
    dockId: string;
    dockNumber: string;
    dockType: string;
    severity: SeverityLevel;
    dwellMinutes: number;
    overMinutes: number; // minutes past the threshold
    occupantAppointment: Appointment;
}

export interface SuggestionTarget {
    appointment: Appointment;
    targetDockId: string;
    targetDockNumber: string;
    reasons: string[];
}

export interface DockSuggestionGroup {
    breachedDock: BreachedDock;
    suggestions: SuggestionTarget[];
    /** When suggestions is empty, explains why (shown in UI) */
    emptyReason?: string;
}

interface DockInfo {
    id: string;
    dockNumber: string;
    type: string;
    status: string;
}

// ─── Status Normalizer ────────────────────────────────────────────────────────

type NormalizedStatus = 'available' | 'occupied' | 'maintenance' | 'out_of_service' | 'other';

/**
 * Robust status normalizer that handles common backend/UI string variants.
 * "Out of Service", "OOS", "out-of-service" → out_of_service
 * "Maintenance", "MAINTENANCE", "maint" → maintenance
 */
function normalizeDockStatus(raw?: string): NormalizedStatus {
    if (!raw) return 'other';
    const s = raw.toLowerCase().replace(/[\s_-]+/g, '');
    if (s === 'available' || s === 'open' || s === 'idle') return 'available';
    if (s === 'occupied' || s === 'busy' || s === 'active') return 'occupied';
    if (s.includes('maint')) return 'maintenance';
    if (s === 'oos' || s.includes('outofservice')) return 'out_of_service';
    return 'other';
}

// ─── Step A: Identify breached docks ──────────────────────────────────────────

export function findBreachedDocks(
    activeOccupants: Appointment[],
    docks: DockInfo[],
    now: number,
): BreachedDock[] {
    const breached: BreachedDock[] = [];

    for (const occ of activeOccupants) {
        if (!occ.checkInTime) continue;
        const checkInMs = safeDateMs(occ.checkInTime);
        if (isNaN(checkInMs)) continue;

        const dwellMinutes = (now - checkInMs) / 60000;
        if (dwellMinutes < SLA_WARN_MINUTES) continue;

        const dockId = occ.dockId || occ.dock_id;
        if (!dockId) continue;

        const dock = docks.find(d => d.id === dockId);
        if (!dock) continue;

        const severity: SeverityLevel =
            dwellMinutes > SLA_CRITICAL_MINUTES ? 'CRITICAL' : 'OVER_SLA';
        const threshold =
            severity === 'CRITICAL' ? SLA_CRITICAL_MINUTES : SLA_WARN_MINUTES;

        breached.push({
            dockId,
            dockNumber: dock.dockNumber,
            dockType: dock.type,
            severity,
            dwellMinutes: Math.floor(dwellMinutes),
            overMinutes: Math.max(1, Math.floor(dwellMinutes - threshold)),
            occupantAppointment: occ,
        });
    }

    // Sort: CRITICAL first, then by dwell descending
    breached.sort((a, b) => {
        if (a.severity !== b.severity) {
            return a.severity === 'CRITICAL' ? -1 : 1;
        }
        return b.dwellMinutes - a.dwellMinutes;
    });

    return breached;
}

// ─── Step B: Find impacted upcoming appointments ──────────────────────────────

function findImpactedAppointments(
    breachedDockId: string,
    todaySchedule: Appointment[],
    now: number,
): Appointment[] {
    const threeHoursMs = 3 * 60 * 60 * 1000;
    const cutoff = now + threeHoursMs;

    return todaySchedule
        .filter(a => {
            if (!['approved', 'scheduled'].includes(a.status)) return false;
            const dockId = a.dockId || a.dock_id;
            if (dockId !== breachedDockId) return false;
            const startMs = safeDateMs(a.startAt);
            return !isNaN(startMs) && startMs >= now && startMs <= cutoff;
        })
        .sort((a, b) => safeDateMs(a.startAt) - safeDateMs(b.startAt))
        .slice(0, 3);
}

// ─── Step C: Find candidate alternative docks ─────────────────────────────────

function findCandidateDocks(
    docks: DockInfo[],
    activeOccupants: Appointment[],
    preferredType?: string,
): DockInfo[] {
    // Build set of currently-occupied dock IDs
    const occupiedDockIds = new Set<string>();
    for (const occ of activeOccupants) {
        const did = occ.dockId || occ.dock_id;
        if (did) occupiedDockIds.add(did);
    }

    return docks
        .filter(d => {
            // Must be available (no active occupant)
            if (occupiedDockIds.has(d.id)) return false;
            // Exclude occupied, maintenance, and OOS via normalizer
            const ns = normalizeDockStatus(d.status);
            if (ns === 'occupied' || ns === 'maintenance' || ns === 'out_of_service') return false;
            return true;
        })
        .sort((a, b) => {
            // Prefer matching type
            if (preferredType) {
                const aMatch = a.type === preferredType ? 0 : 1;
                const bMatch = b.type === preferredType ? 0 : 1;
                if (aMatch !== bMatch) return aMatch - bMatch;
            }
            // Stable tie-breaker: lowest dockNumber
            const numA = parseInt(a.dockNumber.replace(/\D/g, '')) || 0;
            const numB = parseInt(b.dockNumber.replace(/\D/g, '')) || 0;
            return numA - numB;
        });
}

// ─── Step D: Count stacking conflicts for a dock in a time window ─────────────

function countOverlaps(
    dockId: string,
    windowStartMs: number,
    windowEndMs: number,
    todaySchedule: Appointment[],
): number {
    return todaySchedule.filter(a => {
        const did = a.dockId || a.dock_id;
        if (did !== dockId) return false;
        if (!['approved', 'scheduled', 'checked_in', 'in_progress'].includes(a.status)) return false;
        const aStart = safeDateMs(a.startAt);
        const aEnd = safeDateMs(a.endAt);
        if (isNaN(aStart) || isNaN(aEnd)) return false;
        // Overlap check
        return aStart < windowEndMs && aEnd > windowStartMs;
    }).length;
}

// ─── Step E: Build reason tags ────────────────────────────────────────────────

function buildReasons(
    candidateDock: DockInfo,
    appointment: Appointment,
    todaySchedule: Appointment[],
): string[] {
    const reasons: string[] = [];
    reasons.push('Clears congestion');

    // Check if moving reduces stacking on the candidate dock
    const startMs = safeDateMs(appointment.startAt);
    const endMs = safeDateMs(appointment.endAt);
    if (!isNaN(startMs) && !isNaN(endMs)) {
        const overlaps = countOverlaps(candidateDock.id, startMs, endMs, todaySchedule);
        if (overlaps === 0) {
            reasons.push('No stacking');
        }
    }

    if (appointment.delayRiskLevel === 'High') {
        reasons.push('Reduces delay exposure');
    }

    return reasons;
}

// ─── Main: computeRecoverySuggestions ─────────────────────────────────────────

export function computeRecoverySuggestions(
    activeOccupants: Appointment[],
    todaySchedule: Appointment[],
    docks: DockInfo[],
    now: number,
): DockSuggestionGroup[] {
    const breached = findBreachedDocks(activeOccupants, docks, now);
    if (breached.length === 0) return [];

    const groups: DockSuggestionGroup[] = [];

    for (const bd of breached) {
        const impacted = findImpactedAppointments(bd.dockId, todaySchedule, now);

        // No upcoming appointments → still show the breached dock
        if (impacted.length === 0) {
            groups.push({
                breachedDock: bd,
                suggestions: [],
                emptyReason: 'No upcoming appointments on this dock in the next 3 hours.',
            });
            continue;
        }

        // Find candidates with type matching for this breached dock
        const candidateDocks = findCandidateDocks(docks, activeOccupants, bd.dockType);

        // No available docks → still show the breached dock
        if (candidateDocks.length === 0) {
            groups.push({
                breachedDock: bd,
                suggestions: [],
                emptyReason: 'No available docks to move into.',
            });
            continue;
        }

        const suggestions: SuggestionTarget[] = [];
        for (const appt of impacted) {
            // Pick best candidate for this appointment
            const startMs = safeDateMs(appt.startAt);
            const endMs = safeDateMs(appt.endAt);
            let bestCandidate = candidateDocks[0];
            let bestOverlaps = Infinity;

            for (const cand of candidateDocks.slice(0, 5)) { // check top 5 for perf
                const overlaps = countOverlaps(cand.id, startMs, endMs, todaySchedule);
                if (overlaps < bestOverlaps) {
                    bestOverlaps = overlaps;
                    bestCandidate = cand;
                    if (overlaps === 0) break; // can't do better
                }
            }

            suggestions.push({
                appointment: appt,
                targetDockId: bestCandidate.id,
                targetDockNumber: bestCandidate.dockNumber,
                reasons: buildReasons(bestCandidate, appt, todaySchedule),
            });
        }

        groups.push({ breachedDock: bd, suggestions });
    }

    return groups;
}
